import { Trans } from "@lingui/react/macro"
import { Tooltip } from "@mantine/core"
import { IconChartBar } from "@tabler/icons-react"
import { EditableAnalysisField } from "../../../EditableAnalysisField"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * QualityCard renders three derived sub-scores (Balance / Substance /
 * Dominance) and an overall composite alongside the analyser's note. The
 * sub-scores are derived from the existing `quality.*` fields:
 *
 *   - balance: `balance_score` clamped to 0..10 (analyser emits 0..100)
 *   - substance: high → 9, medium → 6.5, low → 4
 *   - dominance: contains("balanced") → 8.5, non-empty → 5, empty → 6
 *
 * Each row's bar gets a `data-tone` of `warn` (<7.5) or `danger` (<6) for
 * the visual cue; default tone uses the brand palette.
 *
 * Sits in the right-hand insights rail with the `info` rail variant.
 */
export function QualityCard({ quality, editContext }: IProps): React.JSX.Element | null {
  if (!quality) return null
  const balanceScore = Math.max(0, Math.min(10, (quality.balance_score ?? 0) / 10))
  const substanceScore = quality.substance_rating === "high" ? 9 : quality.substance_rating === "low" ? 4 : 6.5
  const dominance = (quality.dominance ?? "").toLowerCase()
  const dominanceScore = dominance.includes("balanced") ? 8.5 : dominance ? 5 : 6
  const overall = Math.round(((balanceScore + substanceScore + dominanceScore) / 3) * 10) / 10

  // Hover hints — high vs low describes the *direction* the score points so
  // a user reading "5.2/10 Dominance" knows whether that's good (no single
  // voice took over) or bad (the meeting got drowned out by one speaker).
  const rows = [
    {
      key: "balance",
      label: <Trans>Balance</Trans>,
      score: balanceScore,
      tip: (
        <Trans>
          How evenly contribution was spread across attendees. High = everyone joined in; low = a few voices carried it.
        </Trans>
      ),
    },
    {
      key: "substance",
      label: <Trans>Substance</Trans>,
      score: substanceScore,
      tip: (
        <Trans>How much depth the discussion reached. High = decisions and concrete content; low = surface chat.</Trans>
      ),
    },
    {
      key: "dominance",
      label: <Trans>Dominance</Trans>,
      score: dominanceScore,
      tip: (
        <Trans>
          Whether one speaker dominated airtime. High = balanced floor; low = one voice took most of the room.
        </Trans>
      ),
    },
  ]

  return (
    <section className={classes.insight}>
      <div className={classes.insightHead}>
        <IconChartBar size={16} color="var(--mantine-color-highlight-4)" />
        <h3>
          <Trans>Quality</Trans>
        </h3>
        <span className={classes.insightScore}>{overall.toFixed(1)}/10</span>
      </div>
      {(quality.note || editContext) && (
        <p className={classes.insightBody}>
          {editContext ? (
            <EditableAnalysisField
              meetingId={editContext.meetingId}
              fieldPath="quality.note"
              value={quality.note || ""}
              canEdit={editContext.canEdit}
              isEdited={!!editContext.fieldEdits["quality.note"]}
              onSaved={editContext.onEdited}
            />
          ) : (
            quality.note
          )}
        </p>
      )}
      <div className={classes.qualityRows}>
        {rows.map((row) => {
          const tone = row.score < 6 ? "danger" : row.score < 7.5 ? "warn" : undefined
          return (
            <div key={row.key} className={classes.qualityRow}>
              <Tooltip label={row.tip} multiline w={260} withArrow position="top" openDelay={250}>
                <span className={classes.qualityLbl} style={{ cursor: "help" }}>
                  {row.label}
                </span>
              </Tooltip>
              <span className={classes.qualityNum}>{row.score.toFixed(1)}/10</span>
              <div className={classes.qualityBar}>
                <span
                  className={classes.qualityFill}
                  data-tone={tone}
                  style={{ width: `${row.score * 10}%`, display: "block" }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
